import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as FileUploadIcon } from '../../assets/icons/file-icon.svg';
import { ReactComponent as RemoveFileIcon } from '../../assets/icons/remove-icon.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/tables/document-file.svg';
import { MultiMediaData } from '../../interface/account-management.interface';
import { TooltipIcon } from '../tooltip/TooltipIcon';

interface CustomInputProperties {
  inputLabel?: string;
  acceptedFileType: string;
  placeholder: string;
  selectedFiles: File[] | null;
  name: string;
  onChangeValue: (name: string, file: File[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  unselectHandler?: (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => void;
  existingName?: string;
  existingFiles?: MultiMediaData[] | null;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  tooltipText?: string;
  maxFileSizeMB?: number;
  maxFileCount?: number;
}

/**
 * Format a max file size (given in MB) into a human readable label.
 * Sizes below 1MB are shown in KB (eg. 0.5 -> "500KB"), 1 and above in MB.
 * @param {number} sizeInMB - The max file size in MB
 * @returns {string} the formatted size label
 */
const formatMaxFileSize = (sizeInMB: number): string =>
  sizeInMB < 1 ? `${Math.round(sizeInMB * 1000)}KB` : `${sizeInMB}MB`;

const CHIP_WIDTH = 99;
const CHIP_GAP = 15;
const COUNT_TAG_WIDTH = 34;

/**
 * How many file chips fit on one line of the given container width before a
 * "+N" count tag is needed for the rest.
 * @param {number} containerWidth - Available width of the selected-file-wrap element
 * @param {number} totalItems - Total number of files (existing + newly selected)
 * @returns {number} number of chips that should be rendered
 */
const calculateVisibleChipCount = (containerWidth: number, totalItems: number): number => {
  if (totalItems === 0 || containerWidth <= 0) {
    return 0;
  }

  const widthForAllChips = totalItems * CHIP_WIDTH + (totalItems - 1) * CHIP_GAP;
  if (widthForAllChips <= containerWidth) {
    return totalItems;
  }

  const availableForChips = containerWidth - COUNT_TAG_WIDTH - CHIP_GAP;
  const fittingChips = Math.floor((availableForChips + CHIP_GAP) / (CHIP_WIDTH + CHIP_GAP));
  return Math.min(totalItems, Math.max(1, fittingChips));
};

/**
 * File upload input box
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.acceptedFileType - The acceptedFileType prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.selectedFiles - The selectedFiles prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.unselectHandler - The unselectHandler prop
 * @param {any} param0.existingName - The existingName prop
 * @param {any} param0.existingFiles - The existingFiles prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.disabled - The disabled prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.maxFileSizeMB - Max allowed size per file, in MB
 * @param {any} param0.maxFileCount - Max number of files allowed to be selected
 */
function CustomFileInput({
  inputLabel,
  acceptedFileType,
  name,
  placeholder,
  selectedFiles,
  onChangeValue,
  valueRequired,
  readOnly,
  unselectHandler,
  existingName,
  existingFiles,
  error,
  errorMessage,
  disabled,
  tooltipText,
  maxFileSizeMB,
  maxFileCount,
}: CustomInputProperties) {
  const inputReference = useRef<HTMLInputElement | null>(null);
  const reference = useRef<HTMLDivElement>(null);
  const selectedFileWrapReference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});
  const [sizeError, setSizeError] = useState<string>('');
  const [visibleChipCount, setVisibleChipCount] = useState<number>(0);

  const totalFileCount = (existingFiles?.length || 0) + (selectedFiles?.length || 0);

  /**
   * Recalculate how many file chips fit in the selected-file-wrap on mount
   * and whenever it resizes or the file count changes, so overflow files
   * collapse into the "+N" count tag instead of wrapping/clipping.
   */
  useEffect((): (() => void) => {
    const wrapElement = selectedFileWrapReference.current;
    if (!wrapElement) {
      return () => {};
    }

    /**
     * Measure the current wrap width and update visibleChipCount accordingly.
     */
    const recalculateVisibleChips = (): void => {
      setVisibleChipCount(calculateVisibleChipCount(wrapElement.clientWidth, totalFileCount));
    };

    recalculateVisibleChips();
    const resizeObserver = new ResizeObserver(recalculateVisibleChips);
    resizeObserver.observe(wrapElement);
    return () => resizeObserver.disconnect();
  }, [totalFileCount]);

  /**
   * Function to handle select file. Validates the newly selected files
   * against maxFileSizeMB and maxFileCount (when provided) before passing
   * them up via onChangeValue.
   * @param {any} selectedFileList - The selectedFileList
   */
  const handleSelectFile = (selectedFileList: Array<File>) => {
    if (!selectedFileList?.length) {
      return;
    }

    if (maxFileCount) {
      const totalCount =
        (existingFiles?.length || 0) + (selectedFiles?.length || 0) + selectedFileList.length;
      if (totalCount > maxFileCount) {
        setSizeError(
          `You can select up to ${maxFileCount} ${maxFileCount === 1 ? 'file' : 'files'} only`,
        );
        return;
      }
    }

    if (maxFileSizeMB) {
      const maxSizeInBytes = maxFileSizeMB * 1024 * 1024;
      const hasOversizedFile = selectedFileList.some((file) => file.size > maxSizeInBytes);
      if (hasOversizedFile) {
        setSizeError(`File size should not exceed ${formatMaxFileSize(maxFileSizeMB)}`);
        return;
      }
    }

    setSizeError('');
    onChangeValue(name, selectedFileList);
  };

  /**
   * Function to handle file unselect
   * @param {any} url - The url
   * @param {any} indexToRemove - The indexToRemove
   * @param {any} type - The type
   */
  const handleRemoveSelectedFile = (
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    setSizeError('');
    if (unselectHandler && type === 'newFile') {
      unselectHandler(name, url, indexToRemove, type);
    } else if (unselectHandler && type === 'existingFile' && existingName) {
      unselectHandler(existingName, url, indexToRemove, type);
    } else {
      const afterRemovedSelectedFile =
        selectedFiles?.filter((_, index: number) => index !== indexToRemove) || [];
      onChangeValue(name, afterRemovedSelectedFile);
    }
  };

  /**
   * Handle the close th drop down function
   * @param {any} event - The event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
    }
  };

  /**
   * Get the dropdown position
   * @param {any} event - The event
   */
  const openDropDown = (event: any) => {
    if (existingFiles?.length === 1) {
      window.open(existingFiles[0].signedUrl, '_block');
    } else {
      const rect = event.target.closest('.custom-file-input-container').getBoundingClientRect();
      setStyle({
        top: rect.top + rect.height,
        left: rect.left,
      });
      setOpen('open');
    }
  };

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!sizeError) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSizeError('');
    }, 2000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [sizeError]);

  return (
    <div className="custom-file-input-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && <TooltipIcon text={tooltipText} />}
        </p>
      )}
      <div className={`custom-file-input-container ${(error || sizeError) && 'error'}`}>
        <div className="selected-file-wrap" ref={selectedFileWrapReference}>
          {totalFileCount === 0 && <p className="placeholder">{placeholder}</p>}
          {existingFiles?.length
            ? existingFiles.map((existingFile, index) => {
                if (index >= visibleChipCount) {
                  return null;
                }
                return (
                  <div className="single-selected-file">
                    <span className="doc-icon">
                      <DocumentIcon />
                    </span>
                    <p>{existingFile?.fileName}</p>
                    {!readOnly && (
                      <span
                        className="remove-icon"
                        role="presentation"
                        onClick={() =>
                          handleRemoveSelectedFile(existingFile.url, index, 'existingFile')
                        }>
                        <RemoveFileIcon />
                      </span>
                    )}
                  </div>
                );
              })
            : null}
          {selectedFiles?.length
            ? selectedFiles.map((selectedFile, index) => {
                const combinedIndex = (existingFiles?.length || 0) + index;
                if (combinedIndex >= visibleChipCount) {
                  return null;
                }
                return (
                  <div className="single-selected-file">
                    <span className="doc-icon">
                      <DocumentIcon />
                    </span>
                    <p>{selectedFile?.name}</p>
                    {!readOnly && (
                      <span
                        className="remove-icon"
                        role="presentation"
                        onClick={() => handleRemoveSelectedFile('', index, 'newFile')}>
                        <RemoveFileIcon />
                      </span>
                    )}
                  </div>
                );
              })
            : null}
          {totalFileCount > visibleChipCount ? (
            <div className="count-tag">+{totalFileCount - visibleChipCount}</div>
          ) : null}
        </div>
        <input
          ref={inputReference}
          type="file"
          accept={acceptedFileType}
          multiple
          style={{ display: 'none' } as React.CSSProperties}
          onChange={(event) => {
            const files = event?.target?.files;
            if (files) {
              handleSelectFile(Array.from(files));
              // Reset input value to allow selecting the same file again
              /* eslint-disable no-param-reassign */
              event.target.value = '';
            }
          }}
        />
        <div className="file-input-icon-wrap">
          {readOnly ? (
            <span className="file-input-icon" role="presentation" onClick={openDropDown}>
              <EyeIcon />
            </span>
          ) : (
            <span
              className={`file-input-icon ${disabled && 'disabled'}`}
              role="presentation"
              onClick={() => {
                if (!readOnly && !disabled) {
                  inputReference.current?.click();
                }
              }}>
              <FileUploadIcon />
            </span>
          )}
        </div>
      </div>
      {sizeError ? (
        <p className="text-danger">{sizeError}</p>
      ) : (
        error && <p className="text-danger">{errorMessage}</p>
      )}
      {ReactDOM.createPortal(
        <div className={`dropdown-items-list ${open}`} ref={reference} style={style}>
          {existingFiles?.map((option) => (
            <div
              className="items"
              onClick={() => {
                window.open(option.signedUrl, '_block');
              }}
              role="presentation">
              {option?.fileName}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

CustomFileInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  unselectHandler: null,
  existingName: '',
  existingFiles: [],
  error: false,
  errorMessage: '',
  disabled: false,
  tooltipText: '',
  maxFileSizeMB: undefined,
  maxFileCount: undefined,
};

export default CustomFileInput;
